import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import * as THREE from 'three';
import { Starfield } from '../starfield';
import { Footer } from '../components/layout/Footer';

export default function Impressum() {
  const containerRef = useRef(null);

  // Initialize Starfield
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    camera.position.z = 5;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    container.appendChild(renderer.domElement);

    const starfield = new Starfield(scene);

    const animate = () => {
      requestAnimationFrame(animate);
      starfield.update(window.scrollY);
      renderer.render(scene, camera);
    };
    animate();

    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      container.removeChild(renderer.domElement);
      renderer.dispose();
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white relative overflow-x-hidden">
      {/* Starfield Background */}
      <div ref={containerRef} className="fixed inset-0 z-0" />

      {/* Content */}
      <div className="relative z-10 max-w-4xl mx-auto px-6 pt-16 pb-16">
        {/* White Card */}
        <div className="bg-white rounded-2xl sm:rounded-3xl p-8 md:p-12 shadow-2xl">
          <h1 className="text-4xl md:text-5xl font-bold mb-2">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-pink-500">
              Impressum
            </span>
          </h1>
          <p className="text-gray-500 mb-10">Angaben gemäß § 5 TMG</p>

          <div className="space-y-10 text-gray-700">
            {/* Contact Info Grid */}
            <div className="grid md:grid-cols-2 gap-4 md:gap-8">
              <section className="bg-gray-50 rounded-xl md:rounded-2xl p-4 md:p-6">
                <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-2 md:mb-4 flex items-center gap-2">
                  <svg className="w-4 h-4 md:w-5 md:h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Verantwortlich
                </h2>
                <p className="leading-relaxed text-gray-600 text-sm md:text-base">
                  Robert Geyer<br />
                  Lörenskogstr. 16<br />
                  85748 Garching
                </p>
              </section>

              <section className="bg-gray-50 rounded-xl md:rounded-2xl p-4 md:p-6">
                <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-2 md:mb-4 flex items-center gap-2">
                  <svg className="w-4 h-4 md:w-5 md:h-5 text-pink-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Kontakt
                </h2>
                <p className="leading-relaxed text-gray-600 text-sm md:text-base">
                  Telefon: 0170-1008899<br />
                  E-Mail: robert.geyer156@gmail.com
                </p>
              </section>
            </div>

            <section className="bg-gray-50 rounded-xl md:rounded-2xl p-4 md:p-6">
              <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-2 md:mb-4 flex items-center gap-2">
                <svg className="w-4 h-4 md:w-5 md:h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                Mitverantwortlich
              </h2>
              <p className="text-gray-600 text-sm md:text-base">Josias Fehsenfeld</p>
            </section>

            <section className="bg-gray-50 rounded-xl md:rounded-2xl p-4 md:p-6">
              <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-2 md:mb-4">
                Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV
              </h2>
              <p className="leading-relaxed text-gray-600 text-sm md:text-base">
                Robert Geyer<br />
                Lörenskogstr. 16<br />
                85748 Garching
              </p>
            </section>

            {/* Legal Sections */}
            <div className="space-y-6 pt-4">
              <h2 className="text-2xl font-bold text-gray-900">Haftungsausschluss</h2>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Haftung für Inhalte</h3>
                <p className="text-sm leading-relaxed text-gray-600">
                  Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene Inhalte auf diesen Seiten nach den
                  allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 TMG sind wir als Diensteanbieter jedoch nicht
                  verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach Umständen
                  zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen. Verpflichtungen zur Entfernung oder
                  Sperrung der Nutzung von Informationen nach den allgemeinen Gesetzen bleiben hiervon unberührt.
                  Eine diesbezügliche Haftung ist jedoch erst ab dem Zeitpunkt der Kenntnis einer konkreten
                  Rechtsverletzung möglich. Bei Bekanntwerden von entsprechenden Rechtsverletzungen werden wir
                  diese Inhalte umgehend entfernen.
                </p>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Haftung für Links</h3>
                <p className="text-sm leading-relaxed text-gray-600">
                  Unser Angebot enthält Links zu externen Webseiten Dritter, auf deren Inhalte wir keinen
                  Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen.
                  Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der
                  Seiten verantwortlich. Die verlinkten Seiten wurden zum Zeitpunkt der Verlinkung auf mögliche
                  Rechtsverstöße überprüft. Rechtswidrige Inhalte waren zum Zeitpunkt der Verlinkung nicht
                  erkennbar. Eine permanente inhaltliche Kontrolle der verlinkten Seiten ist jedoch ohne konkrete
                  Anhaltspunkte einer Rechtsverletzung nicht zumutbar. Bei Bekanntwerden von Rechtsverletzungen
                  werden wir derartige Links umgehend entfernen.
                </p>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Urheberrecht</h3>
                <p className="text-sm leading-relaxed text-gray-600">
                  Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen
                  dem deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art
                  der Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen
                  Zustimmung des jeweiligen Autors bzw. Erstellers. Downloads und Kopien dieser Seite sind
                  nur für den privaten, nicht kommerziellen Gebrauch gestattet.
                </p>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Datenschutz</h3>
                <p className="text-sm leading-relaxed text-gray-600">
                  Die Nutzung unserer Webseite ist in der Regel ohne Angabe personenbezogener Daten möglich.
                  Soweit auf unseren Seiten personenbezogene Daten (beispielsweise Name, Anschrift oder
                  E-Mail-Adressen) erhoben werden, erfolgt dies, soweit möglich, stets auf freiwilliger Basis.
                  Diese Daten werden ohne Ihre ausdrückliche Zustimmung nicht an Dritte weitergegeben.
                </p>
              </section>

              <section>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Werbung</h3>
                <p className="text-sm leading-relaxed text-gray-600">
                  Der Nutzung von im Rahmen der Impressumspflicht veröffentlichten Kontaktdaten durch Dritte
                  zur Übersendung von nicht ausdrücklich angeforderter Werbung und Informationsmaterialien
                  wird hiermit ausdrücklich widersprochen. Die Betreiber der Seiten behalten sich ausdrücklich
                  rechtliche Schritte im Falle der unverlangten Zusendung von Werbeinformationen, etwa durch
                  Spam-Mails, vor.
                </p>
              </section>
            </div>
          </div>
        </div>

        {/* Back Button */}
        <div className="mt-12 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors group"
          >
            <svg
              className="w-5 h-5 group-hover:-translate-x-1 transition-transform"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            Zurück zur Startseite
          </Link>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
}
